import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from "@/components/ui/input-otp";
import { ShieldCheck, Mail, ArrowLeft, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in as admin
    const token = localStorage.getItem("bizkeep_admin_token");
    if (token) {
      navigate("/admin/dashboard");
    }
  }, [navigate]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    
    if (!normalizedEmail) {
      toast.error("Enter your admin email");
      return;
    }

    setLoading(true);
    try {
      setEmail(normalizedEmail);
      const response = await axios.post(`${API}/admin/auth/request-otp`, { email: normalizedEmail });
      if (response.data.success) {
        toast.success("Verification code sent to admin email");
        setStep("otp");
        setCountdown(300);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send verification code";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/auth/verify-otp`, {
        email: email.trim().toLowerCase(),
        otp_code: otp
      });
      
      localStorage.setItem("bizkeep_admin_token", response.data.access_token);
      toast.success("Admin login successful!");
      navigate("/admin/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid verification code";
      toast.error(message);
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/auth/request-otp`, { email: email.trim().toLowerCase() });
      if (response.data.success) {
        toast.success("New verification code sent");
        setCountdown(300);
        setOtp("");
      }
    } catch (error) {
      toast.error("Failed to resend code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="page-shell flex min-h-screen items-center justify-center p-6">
      <div className="surface-card w-full max-w-[400px] p-8">
        <div className="mb-8 flex items-center justify-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary">
            <ShieldCheck className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-heading text-xl font-semibold">Admin Portal</span>
        </div>

        {step === "email" ? (
          <>
            <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-2 text-center">
              Admin Login
            </h1>
            <p className="text-slate-500 mb-6 text-center text-sm">
              Enter the admin email to access the dashboard
            </p>

            <form onSubmit={handleRequestOTP} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">
                  Admin Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 pl-12 rounded-lg border-slate-200"
                    data-testid="admin-email-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="h-12 w-full rounded-full font-semibold"
                disabled={loading}
                data-testid="admin-send-code-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Login Code"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <a href="/" className="text-sm text-slate-500 hover:text-slate-700">
                ← Back to Directory
              </a>
            </div>
          </>
        ) : (
          <>
            <button
              onClick={() => {
                setStep("email");
                setOtp("");
              }}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-2 text-center">
              Enter verification code
            </h1>
            <p className="text-slate-500 mb-6 text-center text-sm">
              Code sent to <span className="font-medium">{email}</span>
            </p>

            <div className="space-y-6">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={(value) => setOtp(value)}
                  data-testid="admin-otp-input"
                  disabled={loading}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="w-11 h-12 text-lg border-slate-200" />
                    <InputOTPSlot index={1} className="w-11 h-12 text-lg border-slate-200" />
                    <InputOTPSlot index={2} className="w-11 h-12 text-lg border-slate-200" />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} className="w-11 h-12 text-lg border-slate-200" />
                    <InputOTPSlot index={4} className="w-11 h-12 text-lg border-slate-200" />
                    <InputOTPSlot index={5} className="w-11 h-12 text-lg border-slate-200" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {countdown > 0 && (
                <p className="text-center text-slate-500 text-sm">
                  Code expires in <span className="font-medium">{formatTime(countdown)}</span>
                </p>
              )}

              <Button
                onClick={handleVerifyOTP}
                className="w-full h-12 rounded-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] font-bold"
                disabled={loading || otp.length !== 6}
                data-testid="admin-verify-btn"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify & Login"
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full h-10 text-slate-500 hover:text-slate-700"
                onClick={handleResendOTP}
                disabled={loading || countdown > 240}
                data-testid="admin-resend-btn"
              >
                {countdown > 240 ? `Resend in ${formatTime(countdown - 240)}` : "Resend Code"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminLoginPage;
